"use client";

import { useCallback, useMemo, useState } from "react";
import { PillGroup } from "@/components/tool-kit/primitives";
import {
  SourceCitationBuilder,
  type SourceField,
  type SourceValues,
} from "@/components/tool-kit/source-citation-builder";
import { useToolUi } from "@/components/tools/tool-ui";
import {
  SHAKESPEARE_FIELDS as F,
  formatShakespeare,
  type ShakespeareBook,
  type ShakespeareLabels,
  type ShakespeareWork,
  shakespeareIsReady,
} from "@/lib/tool-kit/citation-sources-books/shakespeare-citation-generator";

/** English strings. A translated content file overrides these through `ui`. */
export const SHAKESPEARE_CITATION_GENERATOR_STRINGS = {
  formHeading: "Edition and passage",
  resultsHeading: "Your citations",
  emptyPrompt: "Enter the title to see your citations.",
  copy: "Copy",
  copied: "Copied!",
  tryExample: "Try an example",
  clear: "Clear",
  workLabel: "What are you citing?",
  workPlay: "A play",
  workSonnets: "The sonnets",
  bookLabel: "Where is it printed?",
  bookOwn: "An edition of its own",
  bookCollected: "Inside a collected works",
  titleLabel: "Title of the play or sonnets",
  titlePlaceholder: "Hamlet",
  titleHint:
    "As the edition prints it, in sentence case. MLA and Chicago get title case automatically.",
  editorsLabel: "Editor(s) of the edition",
  editorsPlaceholder: "Barbara A. Mowat\nPaul Werstine",
  editorsHint:
    "One per line. For a collected works, the editors of the whole book.",
  collectionLabel: "Title of the collected works",
  collectionPlaceholder: "The Norton Shakespeare",
  editionLabel: "Edition of the book (optional)",
  editionPlaceholder: "3rd",
  volumeLabel: "Volume (optional)",
  volumePlaceholder: "2",
  seriesLabel: "Series (MLA and Chicago, optional)",
  seriesPlaceholder: "Folger Shakespeare Library",
  seriesHint:
    "Folger, Arden or Oxford: the series the edition belongs to. APA leaves it out.",
  publisherLabel: "Publisher",
  publisherPlaceholder: "Simon & Schuster",
  yearLabel: "Year of the edition you hold",
  yearPlaceholder: "2012",
  originalYearLabel: "Year first published (APA, optional)",
  originalYearPlaceholder: "1603",
  originalYearHint:
    "APA dates a classic text twice: the original and your edition, as in 1603/2012. Use a date you can support, such as ca. 1600.",
  pagesLabel: "Pages of the play in the book (optional)",
  pagesPlaceholder: "1693-1781",
  urlLabel: "URL, if you read it online (optional)",
  urlPlaceholder: "https://www.folger.edu/",
  actLabel: "Act",
  actPlaceholder: "3",
  sceneLabel: "Scene",
  scenePlaceholder: "1",
  sonnetLabel: "Sonnet number",
  sonnetPlaceholder: "137",
  linesLabel: "Line or lines you quote",
  linesPlaceholder: "64-65",
  linesHint:
    "Line numbers differ between editions: use the numbers in the edition you cite, never a number copied from a quotation website.",
  abbreviationLabel: "Play abbreviation for MLA in-text citations (optional)",
  abbreviationPlaceholder: "Ham.",
  abbreviationHint:
    "MLA recommends the standard abbreviation from the Handbook when you cite more than one play. Type it exactly; the tool never invents one.",
  apaLabel: "APA 7th edition",
  mlaLabel: "MLA 9th edition",
  chicagoLabel: "Chicago 18th edition (notes and bibliography)",
  referenceList: "Reference list",
  parenthetical: "In-text citation (parenthetical)",
  narrative: "In-text citation (narrative)",
  worksCited: "Works Cited",
  inText: "In-text citation",
  footnote: "Footnote",
  shortNote: "Shortened note",
  bibliography: "Bibliography",
  apaNote:
    "APA 7 dates a classic text twice, the year it first appeared and the year of your edition, and puts the editor in parentheses after the title. A series is left out. The act, scene and line locator ends the in-text citation.",
  mlaNote:
    "MLA 9 gives the edition as a book, with the editor and the series, and cites by act.scene.line with Arabic numerals instead of a page. A sonnet is named by number in the citation. Line numbers belong to the edition you cite.",
  chicagoNote:
    "Chicago puts the full edition in the bibliography and cites act.scene.line in the notes, with the sentence References are to act, scene, and line in the first note. Notes for a play inside a collected works and for sonnets follow Chicago's general pattern for part of a book — a convention, not a published example.",
  exampleOwnPlay: {
    title: "Hamlet",
    editors: "Barbara A. Mowat\nPaul Werstine",
    series: "Folger Shakespeare Library",
    publisher: "Simon & Schuster",
    year: "2012",
    originalYear: "1603",
    act: "3",
    scene: "1",
    lines: "64-65",
  },
  exampleOwnSonnets: {
    title: "Shakespeare's sonnets",
    editors: "Katherine Duncan-Jones",
    series: "Arden Shakespeare, Third Series",
    publisher: "Bloomsbury",
    year: "2010",
    originalYear: "1609",
    sonnet: "137",
    lines: "1-2",
  },
  exampleCollectedPlay: {
    title: "Hamlet",
    collection: "The Norton Shakespeare",
    editors:
      "Stephen Greenblatt\nWalter Cohen\nSuzanne Gossett\nJean E. Howard\nKatharine Eisaman Maus",
    edition: "3rd",
    publisher: "W. W. Norton",
    year: "2016",
    originalYear: "1603",
    act: "3",
    scene: "1",
    lines: "64-65",
  },
  exampleCollectedSonnets: {
    title: "Sonnets",
    collection: "The Norton Shakespeare",
    editors:
      "Stephen Greenblatt\nWalter Cohen\nSuzanne Gossett\nJean E. Howard\nKatharine Eisaman Maus",
    edition: "3rd",
    publisher: "W. W. Norton",
    year: "2016",
    originalYear: "1609",
    sonnet: "137",
    lines: "1-2",
  },
} as const;

type Strings = typeof SHAKESPEARE_CITATION_GENERATOR_STRINGS;

function textField(
  id: string,
  label: string,
  placeholder: string,
  hint?: string
): SourceField {
  return { id, kind: "text", label, placeholder, hint };
}

function bookFields(strings: Strings, book: ShakespeareBook): SourceField[] {
  const editors: SourceField = {
    id: F.editors,
    kind: "people",
    label: strings.editorsLabel,
    placeholder: strings.editorsPlaceholder,
    hint: strings.editorsHint,
  };
  if (book === "collected") {
    return [
      textField(
        F.collection,
        strings.collectionLabel,
        strings.collectionPlaceholder
      ),
      editors,
      textField(F.edition, strings.editionLabel, strings.editionPlaceholder),
      textField(F.volume, strings.volumeLabel, strings.volumePlaceholder),
      textField(F.pages, strings.pagesLabel, strings.pagesPlaceholder),
    ];
  }
  return [
    editors,
    textField(F.edition, strings.editionLabel, strings.editionPlaceholder),
    textField(
      F.series,
      strings.seriesLabel,
      strings.seriesPlaceholder,
      strings.seriesHint
    ),
  ];
}

function passageFields(strings: Strings, work: ShakespeareWork): SourceField[] {
  const lines = textField(
    F.lines,
    strings.linesLabel,
    strings.linesPlaceholder,
    strings.linesHint
  );
  if (work === "sonnets") {
    return [
      textField(F.sonnet, strings.sonnetLabel, strings.sonnetPlaceholder),
      lines,
    ];
  }
  return [
    textField(F.act, strings.actLabel, strings.actPlaceholder),
    textField(F.scene, strings.sceneLabel, strings.scenePlaceholder),
    lines,
    textField(
      F.abbreviation,
      strings.abbreviationLabel,
      strings.abbreviationPlaceholder,
      strings.abbreviationHint
    ),
  ];
}

function buildFields(
  strings: Strings,
  work: ShakespeareWork,
  book: ShakespeareBook
): SourceField[] {
  return [
    textField(
      F.title,
      strings.titleLabel,
      strings.titlePlaceholder,
      strings.titleHint
    ),
    ...bookFields(strings, book),
    textField(
      F.publisher,
      strings.publisherLabel,
      strings.publisherPlaceholder
    ),
    {
      id: F.year,
      kind: "date",
      label: strings.yearLabel,
      placeholder: strings.yearPlaceholder,
    },
    textField(
      F.originalYear,
      strings.originalYearLabel,
      strings.originalYearPlaceholder,
      strings.originalYearHint
    ),
    {
      id: F.url,
      kind: "url",
      label: strings.urlLabel,
      placeholder: strings.urlPlaceholder,
    },
    ...passageFields(strings, work),
  ];
}

function exampleFor(
  strings: Strings,
  work: ShakespeareWork,
  book: ShakespeareBook
): SourceValues {
  if (book === "own") {
    return work === "play" ? strings.exampleOwnPlay : strings.exampleOwnSonnets;
  }
  return work === "play"
    ? strings.exampleCollectedPlay
    : strings.exampleCollectedSonnets;
}

/** The widget's strings, as the formatter's labels. */
export function shakespeareLabels(strings: Strings): ShakespeareLabels {
  return {
    apa: strings.apaLabel,
    mla: strings.mlaLabel,
    chicago: strings.chicagoLabel,
    referenceList: strings.referenceList,
    parenthetical: strings.parenthetical,
    narrative: strings.narrative,
    worksCited: strings.worksCited,
    inText: strings.inText,
    footnote: strings.footnote,
    shortNote: strings.shortNote,
    bibliography: strings.bibliography,
    apaNote: strings.apaNote,
    mlaNote: strings.mlaNote,
    chicagoNote: strings.chicagoNote,
  };
}

export default function ShakespeareCitationGeneratorWidget() {
  const strings = useToolUi(SHAKESPEARE_CITATION_GENERATOR_STRINGS);
  const [work, setWork] = useState<ShakespeareWork>("play");
  const [book, setBook] = useState<ShakespeareBook>("own");
  const fields = useMemo(
    () => buildFields(strings, work, book),
    [strings, work, book]
  );
  const labels = useMemo(() => shakespeareLabels(strings), [strings]);
  const format = useCallback(
    (values: SourceValues) => formatShakespeare(work, book, values, labels),
    [work, book, labels]
  );

  return (
    <SourceCitationBuilder
      example={exampleFor(strings, work, book)}
      fields={fields}
      format={format}
      intro={
        <div className="space-y-4">
          <PillGroup
            label={strings.workLabel}
            name="shakespeare-work"
            onChange={setWork}
            options={[
              { value: "play", label: strings.workPlay },
              { value: "sonnets", label: strings.workSonnets },
            ]}
            value={work}
          />
          <PillGroup
            label={strings.bookLabel}
            name="shakespeare-book"
            onChange={setBook}
            options={[
              { value: "own", label: strings.bookOwn },
              { value: "collected", label: strings.bookCollected },
            ]}
            value={book}
          />
        </div>
      }
      isReady={shakespeareIsReady}
      strings={strings}
    />
  );
}
